import type { PublishSubscribe, PublishSubscribeEvent, PublishSubscribeEventListener, SymbolID } from '../types';

class EventData<DataType = unknown> extends Event {
  data?: DataType;
}

class PubSub extends EventTarget {
  private events = new WeakMap<SymbolID, PublishSubscribeEvent>();

  subscribe(id: SymbolID, handler: PublishSubscribeEventListener) {
    const callback = (event: PublishSubscribeEvent) => handler(event, () => this.delete(id, callback));

    this.addEventListener(id.key.description as string, callback);

    if (this.events.has(id)) {
      const event = this.events.get(id) as PublishSubscribeEvent;

      if (event) {
        handler(event, () => this.delete(id, callback));
      }
    }

    return () => this.delete(id, callback);
  }

  unsubscribe(id: SymbolID, handler: PublishSubscribeEventListener) {
    this.removeEventListener(id.key.description as string, handler);
  }

  remove(id: SymbolID) {
    return this.events.delete(id);
  }

  delete(id: SymbolID, handler: PublishSubscribeEventListener) {
    this.remove(id);
    this.removeEventListener(id.key.description as string, handler);
  }

  publish<DataType = any>(id: SymbolID, data: DataType) {
    const event = new EventData<DataType>(id.key.description as string, {
      bubbles: false,
      cancelable: true,
    }) as PublishSubscribeEvent;

    event.data = data;

    this.events.set(id, event);

    queueMicrotask(() => this.dispatchEvent(event));
  }
}

/**
 * Create a publish/subscribe channel.
 */
function createPubSub(): PublishSubscribe {
  const pubsub = new PubSub();

  return {
    publish(id, data) {
      pubsub.publish(id, data);
    },
    remove(id) {
      return pubsub.remove(id);
    },
    subscribe(id, handler) {
      return pubsub.subscribe(id, handler);
    },
    unsubscribe(id, handler) {
      pubsub.unsubscribe(id, handler);
    },
    delete(id, handler) {
      pubsub.delete(id, handler);
    },
  };
}

export { createPubSub };
